import { SERVICE_TYPE, LEGO_CONSTANTS, RESPONSE_TYPE } from 'constants/index';
import { cloneDeep } from 'lodash-es';
import { propertyStore } from 'store';
import getService from 'services/serviceDiscovery';
import JsonEntityPresetSchema from './assets/create-entity-preset';
import {
  CommonUtils,
  buildCollectionsPayload,
  buildCollectionsQueryString,
  getEntityRouteParam,
  getEntityDisplayLabel,
  mapNativeDropdownSources,
  mapNativeDropdownFields,
  mapNativeDropdownSubItems,
  mergeFieldsIntoChoiceSources,
  mergeChoiceSourceData,
  buildDropdownLookupFieldSavePayload,
  buildDependentLookupFields,
  buildDropdownLookupReferenceFieldRequests,
  mergeDropdownLookupReferenceMetadataIntoFields,
} from 'utils';
import { shouldUseTypeBasedEntityApi } from 'utils/entityIdentifier';

// Shifts a custom object's dropdown value when its id equals a native target's lego_id (both share one value space).
const ENTITY_TARGET_COLLISION_OFFSET = 1000000000;
export default class FormBuildController {
  constructor() {
    this._entityID = -1;
    this._dictLookupTargets = null;
    this._lookupTargetsGeneration = 0;
    this._arrCustomizedWidgetFields = null;
    this._choiceDataSources = [];
    this._choiceSourceFieldsCache = {};
    this._choiceSourceSubItemsCache = {};
    this._choiceSourceSubItemFieldsCache = {};
  }

  // singleton class instance
  static getInstance() {
    if (!this.instance) {
      this.instance = new FormBuildController();
    }
    return this.instance;
  }

  cloneObject(objSource) {
    try {
      if (objSource) {
        return cloneDeep(objSource);
      }
      return {};
    } catch (error) {
      console.error('Error cloning object - ' + error);
      return {};
    }
  }

  get i18nTranslate() {
    return this._i18nTranslate;
  }

  set i18nTranslate(value) {
    this._i18nTranslate = value;
  }

  get collections() {
    return this._collections ?? [];
  }

  set collections(value) {
    this._collections = Array.isArray(value) ? value : [];
  }

  set nativeDropdownSourceFieldNames(value) {
    this._nativeDropdownSourceFieldNames = value;
  }

  get nativeDropdownSourceFieldNames() {
    return this._nativeDropdownSourceFieldNames;
  }

  // Native-dropdown/choice-source data is fetched by the host application, not called directly from here -
  // injected from routes/create/index.jsx via useMessageChannelAdapter's getDatasources/getNativeDropdownFields/
  // getNativeDropdownSubItems/getNativeDropdownSubItemFields/getNativeDropdownChoices.
  get nativeDropdownDataSource() {
    return this._nativeDropdownDataSource;
  }

  set nativeDropdownDataSource(value) {
    this._nativeDropdownDataSource = value;
  }

  // datasourceMapper's mapNativeDropdownFields/mapNativeDropdownSources/mapNativeDropdownSubItems all expect
  // the raw API response body - unwrap nativeDropdownDataSource's {success, response, errorMessage} envelope
  // down to that same shape, throwing on failure so the existing try/catch blocks below handle it the same
  // way a rejected direct API call used to.
  __unwrapNativeDropdownResponse = ({ success, response, errorMessage }) => {
    if (!success) throw new Error(errorMessage);
    return response;
  };

  get globalWorkspaceId() {
    return this._globalWorkspaceId ?? null;
  }

  set globalWorkspaceId(value) {
    this._globalWorkspaceId = value ?? null;
  }

  // get the CDN icon prefix URL
  get iconCDNUrl() {
    return `${propertyStore.get(LEGO_CONSTANTS.CDN_PATH)}/${LEGO_CONSTANTS.ICONS}`;
  }

  // return all the default field types supported in "form-builder-field-types.json"
  get jsonPresetSchema() {
    return this.cloneObject(JsonEntityPresetSchema);
  }

  // returns the custom object schema returned from server
  get jsonApiResponse() {
    if (this._dictEntity) {
      if (!this._dictEntity?.fields?.length > 0) {
        const objDefaultField = this.getDefaultPrimaryFieldSchema(this._dictEntity?.name);
        this._dictEntity = { ...this._dictEntity, fields: [objDefaultField] };
      }
      return { ...this._dictEntity };
    }
    return null;
  }

  // returns the custom object id
  get entityID() {
    return this._entityID;
  }

  /** Identifier for routes and type-based API paths (type when v2, else legacy id). */
  get apiEntityId() {
    return getEntityRouteParam(this._dictEntity) ?? this._entityID;
  }

  // check if the entity id is valid
  get isValidEntityID() {
    if (this._entityID !== undefined && !isNaN(this._entityID) && this._entityID > 0) {
      return true;
    }
    return false;
  }

  get customizeWidgetFields() {
    this.removeCustomisedFieldIfNotPresentInEntity();
    return this.cloneObject(this._arrCustomizedWidgetFields);
  }

  get choiceDataSources() {
    return this.cloneObject(this._choiceDataSources);
  }

  destroy = () => {
    this._entityID = -1;
    this._dictEntity = null;
    this._arrCustomizedWidgetFields = null;
    this._choiceDataSources = [];
    this._choiceSourceFieldsCache = {};
    this._choiceSourceSubItemsCache = {};
    this._choiceSourceSubItemFieldsCache = {};
    this._nativeDropdownSourceFieldNames = null;
  };

  hasCustomProperty = (objSource, strProperty) => {
    if (objSource && strProperty && strProperty !== '' && Object.prototype.hasOwnProperty.call(objSource, strProperty)) {
      return true;
    }
    return false;
  };

  // Get default field schema based on the type from the presets json passed
  getDefaultPrimaryFieldSchema = (strFieldName = '') => {
    try {
      const objFieldSchema = cloneDeep(this.jsonPresetSchema.defaultPrimaryFieldBuilderSchema);
      objFieldSchema.label = this._i18nTranslate ? this._i18nTranslate('createEntity.primaryFieldDefaultLabel') : 'Name'; // strFieldName;
      objFieldSchema.isNew = true;
      objFieldSchema.id = 'new-field';
      return objFieldSchema;
    } catch (error) {}
    return null;
  };

  // Get default field schema based on the type from the presets json passed
  getDefaultFieldSchema = (strType, isPrimary = false, strFieldName = '') => {
    try {
      let objDefaultFieldSchema;
      if (isPrimary) {
        objDefaultFieldSchema = cloneDeep(this.jsonPresetSchema.defaultPrimaryFieldBuilderSchema);
        objDefaultFieldSchema.label = strFieldName;
      } else {
        objDefaultFieldSchema = cloneDeep(this.jsonPresetSchema.fieldTypes[strType]);
      }
      objDefaultFieldSchema.isNew = true;
      objDefaultFieldSchema.id = 'new-field';
      return objDefaultFieldSchema;
    } catch (error) {}
    return null;
  };

  // format to return to the form builder after api calls
  buildResponseObject = (boolSuccess = false, objResponse = null, errorMessage = '') => {
    return { success: boolSuccess, response: objResponse, errorMessage: errorMessage };
  };

  // function to build error message and return the error to be displayed
  buildErrorResponseObject = (objError, objResponse = null) => {
    const objErrorData = objError && objError.data ? objError.data : null;
    let strErrorMessage = '';
    if (
      this.hasCustomProperty(objErrorData, 'errors') &&
      objErrorData.errors &&
      objErrorData.errors.length > 0 &&
      this.hasCustomProperty(objErrorData.errors[0], 'message') &&
      objErrorData.errors[0].message !== ''
    ) {
      strErrorMessage = objErrorData.errors[0].message;
    } else if (this.hasCustomProperty(objErrorData, 'message') && objErrorData.message && objErrorData.message !== '') {
      strErrorMessage = objErrorData.message;
    }
    return this.buildResponseObject(false, objResponse, strErrorMessage);
  };

  // function to check if the widget ids are present in the Entity fields
  removeCustomisedFieldIfNotPresentInEntity = () => {
    if (!this._arrCustomizedWidgetFields || this._arrCustomizedWidgetFields.length <= 0) {
      return null;
    }

    if (this._dictEntity && this._dictEntity?.fields) {
      const arrFields = this._dictEntity.fields;
      const intLength = this._arrCustomizedWidgetFields.length;
      for (let i1 = intLength - 1; i1 >= 0; i1--) {
        const strWidgetFieldID = this._arrCustomizedWidgetFields[i1];
        const intWidgetFieldIndex = arrFields.findIndex(el => el.id === strWidgetFieldID);
        if (intWidgetFieldIndex === -1) {
          this._arrCustomizedWidgetFields.splice(i1, 1);
        }
      }
    }
  };

  // function to bring the primary field on top
  bringPrimaryFieldOnTop = arrFields => {
    if (arrFields && arrFields.length > 1) {
      if (arrFields[0].type !== 'PRIMARY') {
        const intPrimaryIndex = arrFields.findIndex(el => el.type === 'PRIMARY');
        if (intPrimaryIndex > 0) {
          const arrPrimaryField = arrFields.splice(intPrimaryIndex, 1);
          arrFields.unshift(arrPrimaryField[0]);
        }
      }
    }
    return arrFields;
  };

  resolveAndCacheSourceFields = async (dataSourceId, subItemId) => {
    const normalizedSourceId = String(dataSourceId);

    if (subItemId != null) {
      const normalizedSubItemId = String(subItemId);
      const cacheKey = `${normalizedSourceId}:${normalizedSubItemId}`;
      if (!this._choiceSourceSubItemFieldsCache[cacheKey]) {
        const objApiResponse = this.__unwrapNativeDropdownResponse(
          await this.nativeDropdownDataSource.getNativeDropdownSubItemFields({
            dropdownId: normalizedSourceId,
            subItemId: normalizedSubItemId,
          })
        );
        this._choiceSourceSubItemFieldsCache[cacheKey] = mapNativeDropdownFields(objApiResponse);
      }
      return this._choiceSourceSubItemFieldsCache[cacheKey];
    }

    if (!this._choiceSourceFieldsCache[normalizedSourceId]) {
      const objApiResponse = this.__unwrapNativeDropdownResponse(await this.nativeDropdownDataSource.getNativeDropdownFields({ dropdownId: normalizedSourceId }));
      this._choiceSourceFieldsCache[normalizedSourceId] = mapNativeDropdownFields(objApiResponse);
    }
    return this._choiceSourceFieldsCache[normalizedSourceId];
  };

  // resolve each distinct datasource/sub-item field list referenced by lookup-backed dropdown fields (via the
  // native-dropdowns / subitemdetail APIs, chosen per field based on its lego_lookup_config datasource +
  // sub-datasource + lookup_field_id), then resolve and merge the data_source/referenceField metadata the
  // choice-source picker needs to show its current selection. Called lazily when a field is expanded, not on load.
  // Shares _choiceSourceFieldsCache/_choiceSourceSubItemFieldsCache with fetchChoiceSourceFields/
  // fetchChoiceSourceSubItemFields, so a source already fetched via either path isn't re-fetched by the other.
  fetchDropdownLookupReferenceMetadata = async arrFields => {
    const arrRequests = buildDropdownLookupReferenceFieldRequests(arrFields);
    if (!arrRequests.length) {
      return arrFields;
    }

    try {
      const arrFieldEntries = await Promise.all(
        arrRequests.map(async ({ dataSourceId, subItemId }) => {
          const normalizedSourceId = String(dataSourceId);
          const requestKey = `${normalizedSourceId}:${subItemId ?? ''}`;
          const sourceFields = await this.resolveAndCacheSourceFields(normalizedSourceId, subItemId);
          return [requestKey, sourceFields];
        })
      );
      return mergeDropdownLookupReferenceMetadataIntoFields(arrFields, new Map(arrFieldEntries));
    } catch (error) {
      console.error('Error fetching dropdown lookup reference field metadata - ' + error);
      return arrFields;
    }
  };

  // After a successful entity load, iterate every field's field_options.lego_lookup_config, dedupe by
  // datasource/sub-datasource, and pre-warm the native-dropdowns / subitems / subitemdetail caches for each -
  // so that a later field expand (which reads these same _choiceSourceFieldsCache / _choiceSourceSubItemsCache /
  // _choiceSourceSubItemFieldsCache caches via fetchChoiceSourceFields / fetchChoiceSourceSubItemFields /
  // fetchDropdownLookupReferenceMetadata) resolves instantly instead of waiting on the network. Fire-and-forget;
  // does not touch _choiceDataSources or any UI-facing state.
  prefetchDropdownLookupChoiceSourceCaches = async () => {
    const arrRequests = buildDropdownLookupReferenceFieldRequests(this._dictEntity?.fields);
    if (!arrRequests.length) {
      return;
    }

    try {
      await Promise.all(
        arrRequests.map(async ({ dataSourceId, subItemId }) => {
          const normalizedSourceId = String(dataSourceId);

          if (subItemId != null) {
            const normalizedSubItemId = String(subItemId);

            if (!this._choiceSourceSubItemsCache[normalizedSourceId]) {
              const objSubItemsResponse = this.__unwrapNativeDropdownResponse(await this.nativeDropdownDataSource.getNativeDropdownSubItems({ dropdownId: normalizedSourceId }));
              this._choiceSourceSubItemsCache[normalizedSourceId] = mapNativeDropdownSubItems(objSubItemsResponse);
            }

            await this.resolveAndCacheSourceFields(normalizedSourceId, normalizedSubItemId);
            return;
          }

          await this.resolveAndCacheSourceFields(normalizedSourceId);
        })
      );
    } catch (error) {
      console.error('Error prefetching dropdown lookup choice source caches - ' + error);
    }
  };

  // get Existing custom object details
  fetchEntity = async numID => {
    // numID is either a legacy numeric id or a type-based route identifier - gate on presence, not numeric-ness
    if (numID) {
      try {
        const apiService = getService(SERVICE_TYPE.ENTITY);
        let objApiResponse = await apiService.get({ id: numID });
        if (this.hasCustomProperty(objApiResponse, 'id') && !isNaN(objApiResponse.id) && objApiResponse.id > 0) {
          this._entityID = objApiResponse.id;
          // condition to check if the primary field is added
          if (!objApiResponse?.fields?.length > 0) {
            const objDefaultField = this.getDefaultPrimaryFieldSchema(objApiResponse?.name);
            objApiResponse = { ...objApiResponse, fields: [objDefaultField] };
          }
          this.bringPrimaryFieldOnTop(objApiResponse.fields);
          this._dictEntity = { ...objApiResponse };
          return this.buildResponseObject(true, { responseType: RESPONSE_TYPE.ENTITY, value: objApiResponse.id });
        } else {
          this._entityID = -1;
          return this.buildResponseObject(false, { responseType: RESPONSE_TYPE.ENTITY, value: null });
        }
      } catch (error) {
        this._entityID = -1;
        console.error('Error fetching entity - ' + error);
        return this.buildErrorResponseObject(error, { responseType: RESPONSE_TYPE.ENTITY, value: null });
      }
    }
  };

  // update the entity icon link url
  updateEntityIcon = (objEntity, strIcon) => {
    if (objEntity && strIcon && strIcon !== '') {
      const arrIconUrl = strIcon.split('/');
      if (arrIconUrl && arrIconUrl.length > 0) {
        const arrIconName = arrIconUrl[arrIconUrl.length - 1].split('.');
        objEntity.icon_link = `${this.iconCDNUrl}/${arrIconName[0]}.svg`;
      }
    }
  };

  // delete local field which was added if there are no fields present
  deleteNewFieldFromPayload = objPayload => {
    if (objPayload) {
      try {
        const arrFields = objPayload.fields;
        const intNewFieldIndex = arrFields.findIndex(e => e.id === 'new-field' && e.isNew === true);
        if (intNewFieldIndex > -1) {
          arrFields.splice(intNewFieldIndex, 1);
        }
      } catch (error) {}
    }
  };

  // merge the host-provided LEGO Collections block into an entity payload.
  // No-op (host collections empty) unless interopFlags.collections is enabled on the host.
  applyHostCollections = objPayload => {
    if (objPayload.collections?.length) return;
    Object.assign(objPayload, buildCollectionsPayload(this.collections));
  };

  // update local object basic details
  getEntityPayload = objDetail => {
    const objEntity = objDetail.value;
    const objPayload = this.cloneObject(this._dictEntity);
    if (shouldUseTypeBasedEntityApi()) {
      // Type-based API: the entered value is the display title; name is never written from the form.
      objPayload.title = objEntity.name;
    } else {
      objPayload.name = objEntity.name;
    }
    objPayload.description = objEntity.description;
    this.updateEntityIcon(objPayload, objEntity.icon_link);
    this.deleteNewFieldFromPayload(objPayload);
    return objPayload;
  };

  // build the payload for creating a new custom object
  getNewEntityPayload = objDetail => {
    this._dictEntity = this.cloneObject(this.jsonPresetSchema.defaultEntitySchema);
    const objPayload = this.getEntityPayload(objDetail);
    if (shouldUseTypeBasedEntityApi()) {
      objPayload.name = null;
    }
    this.applyHostCollections(objPayload);
    return objPayload;
  };

  // create a new custom object
  createNewEntity = async objPayload => {
    try {
      const apiService = getService(SERVICE_TYPE.ENTITY);
      let objApiResponse = await apiService.create({}, objPayload);

      if (this.hasCustomProperty(objApiResponse, 'id') && !isNaN(objApiResponse.id) && objApiResponse.id > 0) {
        this._entityID = objApiResponse.id;
        // condition to check if the primary field is added
        if (!objApiResponse?.fields?.length > 0) {
          const objDefaultField = this.getDefaultPrimaryFieldSchema(objApiResponse?.name);
          objApiResponse = { ...objApiResponse, fields: [objDefaultField] };
        }
        this._dictEntity = { ...objApiResponse };
        return this.buildResponseObject(true, { responseType: RESPONSE_TYPE.CREATE_NEW_ENTITY, id: objApiResponse.id, fieldIndex: -1 });
      } else {
        return this.buildResponseObject(false);
      }
    } catch (error) {
      console.error('Error creating entity - ' + error);
      return this.buildErrorResponseObject(error);
    }
  };

  // datasource native targets are addressed by lego_id, v2/entities targets by type, legacy targets by id
  getRelatedEntityIdForTarget = target => {
    if (target.source === 'entity') return target.type;
    if (target.source === 'datasource') {
      // never fall back to the datasource id - persisting it as related_entity_id silently targets the wrong object
      if (target.lego_id === undefined || target.lego_id === null) {
        console.error('Native lookup target is missing lego_id - ' + target.name);
        return undefined;
      }
      return target.lego_id;
    }
    return target.id;
  };

  // format name - remove the special characters and replace space with underscore for the name
  formatName = strInput => CommonUtils.formatIdentifierName(strInput);

  // function to update the entire custom object
  updateEntity = async objPayload => {
    try {
      const apiService = getService(SERVICE_TYPE.ENTITY);
      delete objPayload.id;

      this.applyHostCollections(objPayload);
      // Fallback to check if the icon is not set to the entity or to set the URL instead of just name
      if (!objPayload.icon_link || objPayload.icon_link === '' || objPayload.icon_link.indexOf('.svg') === -1) {
        this.updateEntityIcon(objPayload, this.jsonPresetSchema.iconSet[0].name);
      }

      // Falback to check if the primary field is not present in the entity
      const arrFields = this.hasCustomProperty(objPayload, 'fields') && objPayload.fields ? objPayload.fields : [];
      if (arrFields && arrFields.length > 0) {
        const arrPrimaryField = arrFields.filter(dataItem => Object.prototype.hasOwnProperty.call(dataItem, 'type') && dataItem.type === 'PRIMARY');
        if (!arrPrimaryField || arrPrimaryField.length === 0) {
          arrFields[0] = { ...arrFields[0], type: 'PRIMARY' };
        }
      }

      let objApiResponse = await apiService.put({ id: this.apiEntityId }, objPayload);
      if (this.hasCustomProperty(objApiResponse, 'id') && !isNaN(objApiResponse.id) && objApiResponse.id > 0) {
        this._entityID = objApiResponse.id;
        // condition to check if the primary field is added
        if (!objApiResponse?.fields?.length > 0) {
          const objDefaultField = this.getDefaultPrimaryFieldSchema(objApiResponse?.name);
          objApiResponse = { ...objApiResponse, fields: [objDefaultField] };
        }
        this._dictEntity = { ...objApiResponse };
        return this.buildResponseObject(true, { responseType: RESPONSE_TYPE.UPDATE_ENTITY, id: objApiResponse.id });
      } else {
        return this.buildResponseObject(false);
      }
    } catch (error) {
      return this.buildErrorResponseObject(error);
    }
  };

  // function to create new field / update existing field details
  getFieldPayload = objDetail => {
    const objFormValues = this.cloneObject(objDetail.value);
    const boolNewField = objDetail.isNew;
    const strFieldType = objFormValues.type;
    const isPrimaryField = !!(this.hasCustomProperty(objFormValues, 'isPrimaryField') && objFormValues.isPrimaryField === true);

    const intIndex = this.hasCustomProperty(objDetail, 'index') ? objDetail.index : -1;
    let intAddedIndex = intIndex;
    let objUpdatedField = null;

    let objPayload = this.cloneObject(this._dictEntity);
    this.deleteNewFieldFromPayload(objPayload);
    this.applyHostCollections(objPayload);

    if (boolNewField) {
      objUpdatedField = this.jsonPresetSchema.defaultFieldSchema;
      if (strFieldType === 'DECIMAL') {
        objUpdatedField.searchable = false;
      }
    } else {
      if (this.hasCustomProperty(objPayload, 'fields') && objPayload.fields && intAddedIndex >= 0 && intAddedIndex < objPayload.fields.length) {
        objUpdatedField = objPayload.fields[intAddedIndex];
      } else {
        console.error('Field not found in entity object..');
        return null;
      }
    }

    const strFieldLabel = objFormValues.name;
    let boolUpdateUniqueValue = true;
    if (strFieldType === 'RELATIONSHIP') {
      // update lookup relationship data if its a new Field
      if (boolNewField) {
        const objRelationshipValues = objFormValues.relationship;
        const objRelatedEntity = this._dictLookupTargets[objRelationshipValues.target];
        objUpdatedField.related_entity_id = this.getRelatedEntityIdForTarget(objRelatedEntity);
        objUpdatedField.relationship_name = this.formatName(strFieldLabel); // needs to be unique within the entity
        objUpdatedField.child_relationship_name = `${this.formatName(objPayload.name ?? objPayload.title)}_${Date.now()}`; // needs to be unique within the entity
        objUpdatedField.field_options.unique = objRelationshipValues.relationship === 'one_to_one';

        // set meta for native target objects
        if (this.hasCustomProperty(objRelatedEntity, 'isNative') && objRelatedEntity.isNative) {
          objUpdatedField.field_options.related_object_type = 'NATIVE';
        }

        // for a non-native custom-object target addressed via v2/entities, merge its type into field_options
        // so the API can resolve the related custom object by type
        if (objRelatedEntity.source === 'entity' && objRelatedEntity.type) {
          objUpdatedField.field_options.custom_object_id = objRelatedEntity.type;
        }

        // Merge in any field_options the picked native_lookup datasource item itself carries (e.g. display/
        // validation config specific to that lookup type) - datasource-supplied keys win on collision.
        if (objRelatedEntity.source === 'datasource' && objRelatedEntity.field_options) {
          objUpdatedField.field_options = { ...objUpdatedField.field_options, ...objRelatedEntity.field_options };
        }
      }
      boolUpdateUniqueValue = false;
    }

    objUpdatedField.type = !isPrimaryField ? strFieldType : 'PRIMARY';
    objUpdatedField.label = strFieldLabel;
    objUpdatedField.required = objFormValues.required;
    objUpdatedField.filterable = this.hasCustomProperty(objFormValues, 'filterable') ? objFormValues.filterable : false;
    objUpdatedField.choices = this.hasCustomProperty(objFormValues, 'choices') && objFormValues.choices && objFormValues.choices.length > 0 ? [...objFormValues.choices] : [];

    if (boolUpdateUniqueValue) {
      objUpdatedField.field_options.unique = this.hasCustomProperty(objFormValues, 'unique') ? objFormValues.unique : false;
    }

    const dropdownLookupFieldSavePayload = buildDropdownLookupFieldSavePayload(objFormValues, this._choiceDataSources, this._nativeDropdownSourceFieldNames);
    if (dropdownLookupFieldSavePayload) {
      // Object.assign is a shallow merge - applying dropdownLookupFieldSavePayload.field_options directly
      // would replace objUpdatedField.field_options wholesale, dropping any pre-existing keys. Merge
      // field_options explicitly instead, but drop `unique` (set above) - DROPDOWN_LOOKUP/DROPDOWN_LOOKUP_SECTION
      // fields are backed by a native source and don't support the unique constraint.
      const { unique, ...remainingFieldOptions } = objUpdatedField.field_options;
      Object.assign(objUpdatedField, dropdownLookupFieldSavePayload, {
        field_options: { ...remainingFieldOptions, ...dropdownLookupFieldSavePayload.field_options },
      });

      // DROPDOWN_LOOKUP_SECTION renders a cascading choice tree (fw-co-nested-select), not a flat select -
      // it doesn't support the standard filter/search-by-choice mechanisms, so both are forced off
      // regardless of what the form submitted.
      if (dropdownLookupFieldSavePayload.type === 'DROPDOWN_LOOKUP_SECTION') {
        objUpdatedField.filterable = false;
        objUpdatedField.searchable = false;

        // The API also expects each cascading dependent level modeled as its own DROPDOWN_LOOKUP
        // sub-field under `fields`. Prefer the field's existing technical name (editing an already-saved
        // field); for a brand new field that name is still blank at this point (normally assigned by the
        // backend on create), so fall back to a client-derived name from the label, same as
        // relationship_name above - and write it back onto the parent field itself so the payload's parent
        // name and its dependent sub-fields' `<name>_dd<level>` prefix are guaranteed to match, instead of
        // sending a blank parent name that the backend could end up naming differently than we guessed.
        const strParentFieldName = objUpdatedField.name || this.formatName(strFieldLabel);
        objUpdatedField.name = strParentFieldName;
        // Preserve backend-assigned ids of existing dependent levels to avoid orphaning record data on update.
        const arrExistingDependentFields = Array.isArray(objUpdatedField.fields) ? objUpdatedField.fields : [];
        objUpdatedField.fields = buildDependentLookupFields(strParentFieldName, strFieldLabel).map(objLevelField => {
          const objExistingLevelField = arrExistingDependentFields.find(objField => objField?.name === objLevelField.name);
          return objExistingLevelField?.id ? { ...objLevelField, id: objExistingLevelField.id } : objLevelField;
        });
      }
    }

    let arrFields = this.hasCustomProperty(objPayload, 'fields') && objPayload.fields ? objPayload.fields : [];
    if (boolNewField) {
      // validate if the array is empty or the passed index is invalid - if true -push the element at the end of the array
      if (arrFields.length === 0 || intIndex < 0 || intIndex > arrFields.length) {
        arrFields = [...arrFields, objUpdatedField];
        objPayload = { ...objPayload, fields: arrFields };
        intAddedIndex = arrFields.length - 1;
      } else {
        // store the element at the passed index
        arrFields = [...arrFields.slice(0, intIndex), objUpdatedField, ...arrFields.slice(intIndex)];
        objPayload = { ...objPayload, fields: arrFields };
        intAddedIndex = intIndex;
      }
    }
    return objPayload;
  };

  // Function to change the position of the fields in the local store
  getRepositionFieldPayload = objDetail => {
    const intSourceIndex = objDetail.sourceIndex;
    const intTargetIndex = objDetail.targetIndex;
    if (intSourceIndex === intTargetIndex) {
      return { success: false };
    }

    try {
      let objPayload = this.cloneObject(this._dictEntity);
      const arrFields = [...objPayload.fields];
      const objField = arrFields.splice(intSourceIndex, 1)[0];
      arrFields.splice(intTargetIndex, 0, objField);
      objPayload = { ...objPayload, fields: arrFields };
      this.applyHostCollections(objPayload);
      return { success: true, payload: objPayload };
    } catch (error) {
      console.error('Error in FormBuildController.js - repositionLocalField - ' + error);
    }
    return { success: false };
  };

  // Function to delete the field item at the specified index
  getDeleteFieldPayload = objDetail => {
    const intIndex = objDetail.index;
    if (intIndex < 0) {
      return { success: false, fieldLabel: '' };
    }

    try {
      let objPayload = this.cloneObject(this._dictEntity);
      const arrFields = [...objPayload.fields];
      const strFieldLabel = arrFields[intIndex].label;
      const strFieldId = arrFields[intIndex].id;
      const isFieldCustomized = this._arrCustomizedWidgetFields && strFieldId && strFieldId !== '' && this._arrCustomizedWidgetFields.indexOf(strFieldId) > -1;

      arrFields.splice(intIndex, 1);
      objPayload = { ...objPayload, fields: arrFields };
      this.applyHostCollections(objPayload);
      return { success: true, payload: objPayload, fieldLabel: strFieldLabel, refreshCustomizedWidgets: isFieldCustomized };
    } catch (error) {
      console.error('Error in FormBuildController.js - deleteLocalFieldAtIndex - ' + error);
    }
    return { success: false, fieldLabel: '', refreshCustomizedWidgets: false };
  };

  // function to save the customised widget fields
  updateCustomisedWidgetFields = async objPayload => {
    if (this._entityID > 0) {
      try {
        const apiService = getService(SERVICE_TYPE.ENTITY);
        const objApiResponse = await apiService.saveCustomisedWidgets({ id: this.apiEntityId }, objPayload);

        if (objApiResponse?.precedence_of_schema_fields && objApiResponse.precedence_of_schema_fields.length > 0) {
          this._arrCustomizedWidgetFields = this.cloneObject(objApiResponse.precedence_of_schema_fields);
          this.removeCustomisedFieldIfNotPresentInEntity();
          return this.buildResponseObject(true, { responseType: RESPONSE_TYPE.WIDGET, value: objApiResponse.precedence_of_schema_fields });
        } else {
          return this.buildResponseObject(false, { responseType: RESPONSE_TYPE.WIDGET, value: null });
        }
      } catch (error) {
        console.error('Error fetching widgets - ' + error);
        return this.buildErrorResponseObject(error, { responseType: RESPONSE_TYPE.WIDGET, value: null });
      }
    }
    return null;
  };

  // get all the widget fields for the entity
  fetchEntityWidgetFields = async numID => {
    if (numID > 0) {
      try {
        const apiService = getService(SERVICE_TYPE.ENTITY);
        const objApiResponse = await apiService.customisedWidgets({ id: numID });

        if (objApiResponse?.precedence_of_schema_fields && objApiResponse.precedence_of_schema_fields.length > 0) {
          this._arrCustomizedWidgetFields = this.cloneObject(objApiResponse.precedence_of_schema_fields);
          this.removeCustomisedFieldIfNotPresentInEntity();
          return this.buildResponseObject(true, { responseType: RESPONSE_TYPE.WIDGET, value: this._arrCustomizedWidgetFields });
        } else {
          return this.buildResponseObject(false, { responseType: RESPONSE_TYPE.WIDGET, value: null });
        }
      } catch (error) {
        console.error('Error fetching widgets - ' + error);
        return this.buildErrorResponseObject(error, { responseType: RESPONSE_TYPE.WIDGET, value: null });
      }
    }
    return null;
  };

  // get Existing target objects for lookup relationship
  fetchLookupTargetObjects = async (nativeObjectData, useDataSourceApi) => {
    try {
      // Each call builds into its own dict and only publishes it if no newer call started meanwhile, so an
      // overlapping/stale fetch cycle (e.g. after destroy()/re-mount) can't overwrite the current targets.
      const generation = ++this._lookupTargetsGeneration;
      const dictTargets = {};
      const arrEntityObjects = useDataSourceApi
        ? await this.fetchLookupTargetObjectsFromDataSourceApi(dictTargets)
        : await this.fetchLookupTargetObjectsLegacy(nativeObjectData, dictTargets);
      if (generation === this._lookupTargetsGeneration) {
        this._dictLookupTargets = dictTargets;
      }
      return this.buildResponseObject(true, { responseType: RESPONSE_TYPE.LOOKUP_TARGETS, value: arrEntityObjects });
    } catch (error) {
      console.error('Error fetching lookup relationships - ' + error);
      return this.buildErrorResponseObject(error, { responseType: RESPONSE_TYPE.LOOKUP_TARGETS, value: null });
    }
  };

  // legacy target list: native objects context + custom-object schemas
  fetchLookupTargetObjectsLegacy = async (nativeObjectData, dictTargets = {}) => {
    const apiService = getService(SERVICE_TYPE.ENTITY);
    const nativeIds = Object.keys(nativeObjectData.idLookup);
    const arrEntityObjects = nativeIds.map(nativeId => {
      const nativeSchema = nativeObjectData.idLookup[nativeId];
      dictTargets[nativeId] = { ...nativeSchema, isNative: true };
      const text = CommonUtils.humanizeString(nativeObjectData.idLookup[nativeId].name);
      return { value: Number(nativeId), text, isNative: true };
    });
    const arrApiResponse = await apiService.getAll();
    for (const schemaIndex in arrApiResponse.schemas) {
      const schema = arrApiResponse.schemas[schemaIndex];
      if (schema.id !== this._entityID) {
        arrEntityObjects.push({ value: schema.id, text: getEntityDisplayLabel(schema), isNative: false });
        dictTargets[schema.id] = { ...schema, isNative: false };
      }
    }
    return arrEntityObjects;
  };

  // Raw query string appended (via getPath's queryString passthrough) only to the two v2/entities calls below,
  // for the lookup-relationship target dropdown specifically - not baked into entityService.getAllV2/searchV2
  // themselves, so no other caller of those shared methods is affected.
  // Combines the host's "current" workspace (from the generic collections framework, PR #862) with the
  // account-level global workspace id into one workspace_id collection, then reuses buildCollectionsQueryString
  // (already builds the repeated-key collections.<type>=A&collections.<type>=B format this needs).
  // Returns null when there's no workspace id at all, so callers send no query params in that case.
  _buildTargetDropdownQueryString = () => {
    const currentWorkspaceIds = this.collections.find(collection => collection.type === 'workspace_id')?.collection_item_ids ?? [];
    const workspaceIds = [...new Set([...currentWorkspaceIds, this.globalWorkspaceId].filter(id => id !== null && id !== undefined))];
    if (workspaceIds.length === 0) {
      return null;
    }
    const workspaceQueryString = buildCollectionsQueryString([{ type: 'workspace_id', collection_item_ids: workspaceIds }]);
    return `${workspaceQueryString}&has_primary=true`;
  };

  // Native targets keep their lego_id as the value (needed for pre-selection), so a colliding custom object is the one shifted.
  getEntityTargetValue = (entity, targets) => (targets[entity.id]?.source === 'datasource' ? ENTITY_TARGET_COLLISION_OFFSET + entity.id : entity.id);

  // target list when useDataSourceApi is enabled: native_lookup (datasources) + v2/entities, fully replacing the legacy list
  fetchLookupTargetObjectsFromDataSourceApi = async (dictTargets = {}) => {
    const datasourceApiService = getService(SERVICE_TYPE.DATASOURCE);
    const entityApiService = getService(SERVICE_TYPE.ENTITY);
    const targetDropdownQueryString = this._buildTargetDropdownQueryString();
    const [objDatasourceResponse, objEntityResponse] = await Promise.all([
      datasourceApiService.getAll(),
      entityApiService.getAllV2({}, targetDropdownQueryString ? { queryString: targetDropdownQueryString } : {}),
    ]);

    // Native targets use their lego_id as the dropdown value: it is what gets saved as related_entity_id, and the form
    // builder pre-selects an existing field's target by matching related_entity_id against the option value. lego_id is
    // also positive and non-zero, which the builder's "target > 0" validation and the dropdown's falsy-value check need.
    const arrNativeLookup = Array.isArray(objDatasourceResponse?.native_lookup) ? objDatasourceResponse.native_lookup : [];
    const arrEntityObjects = arrNativeLookup
      .filter(item => item.lego_id !== undefined && item.lego_id !== null)
      .map(item => {
        dictTargets[item.lego_id] = { ...item, isNative: true, source: 'datasource' };
        return { value: item.lego_id, text: item.label, isNative: true };
      });

    const arrEntities = Array.isArray(objEntityResponse?.entities) ? objEntityResponse.entities : [];
    arrEntities.forEach(entity => {
      if (entity.id !== this._entityID) {
        const value = this.getEntityTargetValue(entity, dictTargets);
        dictTargets[value] = { ...entity, isNative: false, source: 'entity' };
        arrEntityObjects.push({ value, text: getEntityDisplayLabel(entity), isNative: false });
      }
    });

    return arrEntityObjects;
  };

  // Live search for the lookup-relationship target picker (useDataSourceApi path only), passed to crayons as
  // targetSelectProps.search - crayons calls this with (filterText, dataSource) after its own debounce.
  // dataSource is always the full originally-fetched list (crayons reverts to it automatically on clear), so
  // no extra "restore on clear" state is needed here.
  searchLookupTargetObjects = async (filterText, dataSource) => {
    const term = (filterText ?? '').trim();
    if (term.length < 3) {
      return dataSource;
    }
    const localMatches = (dataSource ?? []).filter(item => item.isNative && item.text?.toLowerCase().includes(term.toLowerCase()));
    try {
      const entityApiService = getService(SERVICE_TYPE.ENTITY);
      const targetDropdownQueryString = this._buildTargetDropdownQueryString();
      const objEntityResponse = await entityApiService.searchV2(term, {}, targetDropdownQueryString ? { queryString: targetDropdownQueryString } : {});
      const arrEntities = Array.isArray(objEntityResponse?.entities) ? objEntityResponse.entities : [];
      const searchedEntityObjects = [];
      arrEntities.forEach(entity => {
        if (entity.id !== this._entityID) {
          const value = this.getEntityTargetValue(entity, this._dictLookupTargets);
          this._dictLookupTargets = { ...this._dictLookupTargets, [value]: { ...entity, isNative: false, source: 'entity' } };
          searchedEntityObjects.push({ value, text: getEntityDisplayLabel(entity), isNative: false });
        }
      });
      return [...localMatches, ...searchedEntityObjects];
    } catch (error) {
      console.error('Error searching lookup targets - ' + error);
      return localMatches;
    }
  };

  fetchChoiceDataSources = async () => {
    try {
      const objApiResponse = this.__unwrapNativeDropdownResponse(await this.nativeDropdownDataSource.getDatasources());
      this._choiceDataSources = mapNativeDropdownSources(objApiResponse);
      return this.buildResponseObject(true, { responseType: RESPONSE_TYPE.CHOICE_DATA_SOURCES, value: this.choiceDataSources });
    } catch (error) {
      console.error('Error fetching choice data sources - ' + error);
      return this.buildErrorResponseObject(error, { responseType: RESPONSE_TYPE.CHOICE_DATA_SOURCES, value: null });
    }
  };

  fetchChoiceSourceFields = async sourceId => {
    const normalizedSourceId = String(sourceId ?? '');
    if (!normalizedSourceId) {
      return this.buildResponseObject(false, { type: 'CHOICE_SOURCE_FIELDS', sourceId: normalizedSourceId, value: null });
    }

    try {
      const source = this._choiceDataSources.find(item => String(item.value) === normalizedSourceId);
      const hasSubItems = !!source?.has_sub_items;

      if (hasSubItems) {
        if (!this._choiceSourceSubItemsCache[normalizedSourceId]) {
          const objApiResponse = this.__unwrapNativeDropdownResponse(await this.nativeDropdownDataSource.getNativeDropdownSubItems({ dropdownId: normalizedSourceId }));
          this._choiceSourceSubItemsCache[normalizedSourceId] = mapNativeDropdownSubItems(objApiResponse);
        }

        this._choiceDataSources = mergeChoiceSourceData(this._choiceDataSources, normalizedSourceId, {
          subItems: this._choiceSourceSubItemsCache[normalizedSourceId],
          fields: [],
        });
      } else {
        await this.resolveAndCacheSourceFields(normalizedSourceId);

        this._choiceDataSources = mergeChoiceSourceData(this._choiceDataSources, normalizedSourceId, {
          subItems: [],
          fields: this._choiceSourceFieldsCache[normalizedSourceId],
        });
      }

      return this.buildResponseObject(true, {
        type: 'CHOICE_SOURCE_FIELDS',
        sourceId: normalizedSourceId,
        value: this.choiceDataSources,
      });
    } catch (error) {
      console.error('Error fetching choice source fields - ' + error);
      return this.buildErrorResponseObject(error, { type: 'CHOICE_SOURCE_FIELDS', sourceId: normalizedSourceId, value: null });
    }
  };

  fetchChoiceSourceSubItemFields = async (sourceId, subItemId) => {
    const normalizedSourceId = String(sourceId ?? '');
    const normalizedSubItemId = String(subItemId ?? '');
    if (!normalizedSourceId || !normalizedSubItemId) {
      return this.buildResponseObject(false, {
        type: 'CHOICE_SOURCE_SUBITEM_FIELDS',
        sourceId: normalizedSourceId,
        subItemId: normalizedSubItemId,
        value: null,
      });
    }

    try {
      const fields = await this.resolveAndCacheSourceFields(normalizedSourceId, normalizedSubItemId);

      this._choiceDataSources = mergeFieldsIntoChoiceSources(this._choiceDataSources, normalizedSourceId, fields);

      return this.buildResponseObject(true, {
        type: 'CHOICE_SOURCE_SUBITEM_FIELDS',
        sourceId: normalizedSourceId,
        subItemId: normalizedSubItemId,
        value: this.choiceDataSources,
      });
    } catch (error) {
      console.error('Error fetching choice source sub-item fields - ' + error);
      return this.buildErrorResponseObject(error, {
        type: 'CHOICE_SOURCE_SUBITEM_FIELDS',
        sourceId: normalizedSourceId,
        subItemId: normalizedSubItemId,
        value: null,
      });
    }
  };
}
